import { cn } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { GrCloudUpload } from 'react-icons/gr';
import { MdClose, MdSave } from 'react-icons/md';
import { z } from 'zod';
import { useImageEditor } from '@/core/hooks';
import { ImageCropper } from '@/core/ui';
import { Button, FormFileInput, FormInput, FormSwitch, FormTextArea } from '@/core/ui/Form';
import { Dialog } from '@/core/ui/Overlay';
import { useImage } from '@/store/image';
import ImageTransformControls from './ImageTransformControls';

const schema = z.object({
  file: z
    .file()
    .mime(['image/jpeg', 'image/png', 'image/gif', 'image/webp'], {
      message: 'Only JPG, PNG, GIF, WEBP are supported.',
    })
    .max(4 * 1024 * 1024, { message: `Max File size is 4MB.` }),
  sensitive: z.boolean(),
  name: z.string().min(1, { message: 'Name is required' }),
  caption: z.string().optional().nullable(),
});

type ISchema = z.infer<typeof schema>;

export interface AssetEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (result: ISchema) => Promise<void>;
  defaultValues?: Omit<ISchema, 'file'> & { file: ISchema['file'] | string };
  loading?: boolean;
  isDisabled?: boolean;
  title?: string;
}

export const AssetEditor: React.FC<AssetEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  defaultValues,
  loading,
  title,
  isDisabled,
}) => {
  const { image, canvasRef } = useImageEditor();
  const setImage = useImage.use.setImage();
  const resetTransforms = useImage.use.resetTransforms();
  const setIsCropping = useImage.use.setIsCropping();
  const isCropping = useImage.use.isCropping();
  const rotation = useImage.use.rotation();
  const flipHorizontal = useImage.use.flipHorizontal();
  const flipVertical = useImage.use.flipVertical();
  const loadImage = useImage.use.loadImage();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      caption: defaultValues?.caption ?? '',
      sensitive: defaultValues?.sensitive ?? false,
      name: defaultValues?.name ?? '',
    },
  });

  useEffect(() => {
    if (isOpen && defaultValues?.file) {
      loadImage(defaultValues.file);
    }
  }, [isOpen, defaultValues?.file, loadImage]);

  const resetEditor = () => {
    resetTransforms();
    setIsCropping(false);
    reset();
    if (defaultValues?.file) {
      loadImage(defaultValues.file);
    } else {
      setImage(null);
    }
  };

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      if (blob) {
        // Convert Blob to File
        const file = new File([blob], 'image.png', { type: 'image/png' });
        onSave({
          file,
          name: data.name ?? '',
          caption: data.caption ?? '',
          sensitive: data.sensitive ?? false,
        }).then(() => {
          resetEditor();
        });
      }
    }, 'image/png');
  };

  return (
    <div className=''>
      <Dialog
        isOpen={isOpen}
        onOpenChange={(open) => !open && onClose()}
        title={`${title ?? 'Image Editor'} ${isDisabled ? ' (Save is disabled for trial)' : ''}`}
        footer={(close) => (
          <div className='flex justify-between w-full'>
            <Button
              isDisabled={loading}
              variant='light'
              onPress={() => {
                resetEditor();
                close();
              }}
              startContent={<MdClose />}
            >
              Close
            </Button>
            <div className='flex gap-2'>
              <Button isDisabled={loading} variant='light' onPress={resetEditor}>
                Reset
              </Button>
              <Button
                className='text-background'
                isDisabled={!isValid || isCropping || isDisabled}
                isLoading={loading}
                onPress={() => {
                  handleSubmit(onSubmit)();
                }}
                color='success'
                startContent={<MdSave />}
              >
                {isDisabled ? 'Save (Disabled)' : 'Save'}
              </Button>
            </div>
          </div>
        )}
        // size='5xl'
        radius='sm'
        backdrop='blur'
        footerClassName='py-3 border-t border-default-200'
        classNames={{
          base: 'max-w-[70vw] max-h-[90vh] border border-default-200',
          body: 'p-0',
        }}
        trigger={() => <></>}
      >
        <div className='flex h-[70vh] w-full'>
          {/* Left Panel - Image Preview */}
          <div className='flex-1 w-[40rem] p-4 border-r border-default-200'>
            <div className='h-full flex flex-col'>
              <div className='flex-1 flex items-center border border-dashed border-default-400 justify-center bg-default-50 rounded-lg overflow-hidden'>
                {image ? (
                  <div className='relative w-full h-full'>
                    {isCropping ? (
                      <ImageCropper
                        imageSrc={image.src}
                        defaultTransforms={{
                          rotate: rotation,
                          flip: {
                            horizontal: flipHorizontal,
                            vertical: flipVertical,
                          },
                        }}
                      />
                    ) : (
                      <canvas
                        ref={canvasRef}
                        className='max-w-full max-h-full border border-default-200 rounded m-auto block'
                      />
                    )}
                  </div>
                ) : (
                  <div className='text-center text-default-500 flex flex-col items-center justify-center w-full'>
                    <FormFileInput
                      control={control}
                      name={'file' as any}
                      showPreview={false}
                      options={{
                        accept: {
                          'image/jpeg': ['.jpg', '.jpeg'],
                          'image/png': ['.png'],
                          'image/gif': ['.gif'],
                          'image/webp': ['.webp'],
                        },
                        maxSize: 4 * 1024 * 1024,
                        maxFiles: 1,
                      }}
                      onUpload={(file) => {
                        if (!file) {
                          // if no file, reset editor
                          resetEditor();
                          return;
                        }
                        if (file instanceof File) {
                          setValue('name', file.name, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                          loadImage(file);
                        }
                      }}
                      className='max-w-sm'
                    >
                      <div className='flex py-12 flex-col items-center justify-center gap-2'>
                        <GrCloudUpload className='text-foreground w-10 h-10' />
                        <span className='text-base text-foreground'>
                          Drag & drop an image here, or click to browse
                        </span>
                        <span className='text-xs text-foreground-400'>
                          Supports: JPG, PNG, GIF, WEBP (Max 4MB).
                        </span>
                      </div>
                    </FormFileInput>
                  </div>
                )}
              </div>

              {/* Preview */}
              {/* {image && (
              <div className='mt-4'>
                <h4 className='text-sm font-medium mb-2'>Preview</h4>
                <div className='flex justify-center'>
                  <canvas ref={previewCanvasRef} className='border border-default-200 rounded' />
                </div>
              </div>
            )} */}
            </div>
          </div>

          {/* Right Panel - Configuration */}
          <div
            className={cn('w-80 p-4 space-y-4 overflow-y-auto', {
              'opacity-50 pointer-events-none cursor-not-allowed': loading || !image,
            })}
          >
            <div className='flex flex-col'>
              <h4 className='text-sm font-medium my-3'>Content Settings</h4>
              <FormInput control={control} name='name' placeholder='Enter image name...' variant='bordered' />
              <FormTextArea
                control={control}
                name='caption'
                maxRows={2}
                className='mt-2'
                // label='Caption'
                placeholder='Enter image caption...'
                variant='bordered'
              />

              {/* <span className='text-sm'>Mark as sensitive</span> */}
              <FormSwitch
                classNames={{
                  base: cn(
                    'inline-flex mt-2 flex-row-reverse w-full max-w-full bg-content1 items-center',
                    'justify-between cursor-pointer rounded-lg gap-2 p-2 border-2 border-default-200',
                    'data-[selected=true]:border-primary',
                  ),
                }}
                control={control}
                name='sensitive'
              >
                <div className='flex flex-col justify-center'>
                  <span className='text-sm font-medium text-foreground'>Mark as sensitive?</span>
                  <span className='text-xs text-foreground-400'>The image will appear blurred.</span>
                </div>
              </FormSwitch>
            </div>

            {/* Transform controls */}
            <ImageTransformControls />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default AssetEditor;
